import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, CheckCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollStatus();
    }
  }, [sessionId]);

  const pollStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await paymentsAPI.getStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollStatus, 2000);
    }
  };

  const dashboardLink = user?.account_type === 'employer' ? '/employer/dashboard' : '/employee/dashboard';

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F5F8FF' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div style={{ 
              width: '44px', 
              height: '44px', 
              background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span style={{ 
              fontSize: '24px', 
              fontWeight: 800, 
              color: '#0A0F1E',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}>VETT</span>
          </Link>
        </div>

        <Card style={{ 
          background: '#FFFFFF', 
          border: '1px solid #E8EDF5', 
          borderRadius: '16px', 
          padding: '32px',
          textAlign: 'center'
        }}>
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4" style={{ color: '#1B5AFF' }} />
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: 700, 
                color: '#0A0F1E',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                marginBottom: '8px'
              }}>Processing Payment</h1>
              <p style={{ color: '#5B6472', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'rgba(0, 201, 167, 0.15)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <CheckCircle className="h-10 w-10" style={{ color: '#00C9A7' }} />
              </div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: 700, 
                color: '#0A0F1E',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                marginBottom: '8px'
              }}>Payment Successful!</h1>
              <p style={{ color: '#5B6472', marginBottom: '24px', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Thank you for your purchase. Your account has been upgraded.
              </p>
              <Link to={dashboardLink}>
                <Button style={{ 
                  background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)',
                  color: '#FFFFFF',
                  borderRadius: '10px',
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 500
                }}>
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'rgba(240, 180, 41, 0.15)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Loader2 className="h-10 w-10" style={{ color: '#F0B429' }} />
              </div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: 700, 
                color: '#0A0F1E',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                marginBottom: '8px'
              }}>Still Processing</h1>
              <p style={{ color: '#5B6472', marginBottom: '24px', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Payment is taking longer than expected. Check your email for confirmation.
              </p>
              <Link to={dashboardLink}>
                <Button style={{ 
                  background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)',
                  color: '#FFFFFF',
                  borderRadius: '10px',
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 500
                }}>
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'rgba(229, 72, 77, 0.15)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Shield className="h-10 w-10" style={{ color: '#E5484D' }} />
              </div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: 700, 
                color: '#0A0F1E',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                marginBottom: '8px'
              }}>Session Expired</h1>
              <p style={{ color: '#5B6472', marginBottom: '24px', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                This payment session has expired. Please try again.
              </p>
              <Link to={dashboardLink}>
                <Button style={{ 
                  background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)',
                  color: '#FFFFFF',
                  borderRadius: '10px',
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 500
                }}>
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
